import { useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, BadgePercent, MessageCircle, Calculator, Sun } from "lucide-react";
import { useLanguage } from "../i18n/translations";

const WA_LINK = "https://wa.me/918922004004?text=Hello%20Uma%20Enterprises,%20I%20want%20to%20enquire%20about%20Solar/Wind/AI%20solutions.";
const IMG = "https://images.unsplash.com/photo-1466611653911-95081537e5b7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHw0fHx3aW5kJTIwdHVyYmluZXMlMjBncmVlbiUyMGVuZXJneSUyMGZpZWxkfGVufDB8fHx8MTc5MTgxMjMwOHww&ixlib=rb-4.1.0&q=85";
const IMG2 = "https://images.unsplash.com/photo-1786515284601-f9ac47d0758a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxzb2xhciUyMHJvb2Z0b3AlMjBwYW5lbHMlMjBob21lJTIwaW5zdGFsbGF0aW9ufGVufDB8fHx8MTc5MTgxMjMwOHww&ixlib=rb-4.1.0&q=85";

const MaskedLine = ({ children, delay }) => (
  <span className="mask-line">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { t } = useLanguage();
  const [bill, setBill] = useState(3000);
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 600], [0, 90]);
  const yImg2 = useTransform(scrollY, [0, 600], [0, -60]);

  const kw = Math.max(1, Math.round(bill / 1200));
  const central = Math.min(78000, kw <= 2 ? kw * 30000 : 60000 + (kw - 2) * 18000);
  const subsidy = central + 30000;
  const savings = Math.round(bill * 0.9);

  return (
    <section id="top" className="relative overflow-hidden">
      <div className="pointer-events-none absolute -top-40 left-1/2 h-[600px] w-[900px] -translate-x-1/2 rounded-full bg-solar/10 blur-[140px]" />
      <div className="pointer-events-none absolute right-[-200px] top-1/3 h-[400px] w-[400px] rounded-full bg-eco/10 blur-[120px]" />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: "linear-gradient(rgba(15,32,51,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(15,32,51,.5) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
        }}
      />

      <div className="relative mx-auto grid max-w-7xl gap-12 px-4 pb-20 pt-16 sm:px-6 lg:grid-cols-12 lg:pb-28 lg:pt-24">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-eco/40 bg-eco/10 px-4 py-1.5 text-xs font-semibold text-eco-soft"
            data-testid="hero-vendor-badge"
          >
            <ShieldCheck size={14} /> {t.hero.vendor}
          </motion.div>

          <h1 className="font-display text-4xl font-black leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="hero-title">
            <MaskedLine delay={0.15}>
              {t.hero.line1a} <em className="not-italic text-eco-bright">{t.hero.line1b}</em>
            </MaskedLine>
            <MaskedLine delay={0.3}>
              {t.hero.line2a} <span className="text-stroke-gold">{t.hero.line2b}</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-400 md:text-lg"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.7 }}
            className="mt-7 inline-flex items-center gap-2 rounded-2xl border border-solar/50 bg-gradient-to-r from-solar/20 to-transparent px-5 py-3"
            data-testid="hero-finance-badge"
          >
            <BadgePercent className="text-solar-yellow" size={22} />
            <span className="font-display text-base font-extrabold text-solar-highlight sm:text-lg">{t.hero.finance}</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a
              href="#estimator"
              data-testid="hero-calc-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-solar px-7 py-3.5 text-sm font-bold text-amber-950 shadow-[0_0_40px_rgba(245,158,11,0.35)] transition-all duration-300 hover:bg-solar-yellow hover:shadow-[0_0_60px_rgba(245,158,11,0.5)]"
            >
              <Calculator size={18} className="transition-transform duration-300 group-hover:rotate-12" />
              {t.hero.cta1}
            </a>
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-btn"
              className="inline-flex items-center gap-2 rounded-full border border-eco/50 bg-eco/10 px-7 py-3.5 text-sm font-bold text-eco-bright transition-colors duration-300 hover:bg-eco hover:text-amber-950"
            >
              <MessageCircle size={18} /> {t.hero.cta2}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-12 grid max-w-md grid-cols-3 gap-6 border-t border-white/10 pt-8"
          >
            {[
              { n: t.hero.stat1n, l: t.hero.stat1 },
              { n: t.hero.stat2n, l: t.hero.stat2 },
              { n: t.hero.stat3n, l: t.hero.stat3 },
            ].map((s) => (
              <div key={s.l}>
                <div className="font-mono text-lg font-bold text-white sm:text-2xl">{s.n}</div>
                <div className="mt-1 text-xs uppercase tracking-wider text-slate-500">{s.l}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            style={{ y: yImg }}
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 0.4 }}
            className="absolute -top-6 right-0 hidden h-44 w-56 rotate-3 overflow-hidden rounded-2xl border border-white/10 shadow-2xl lg:block"
          >
            <img src={IMG} alt="Wind turbines" className="h-full w-full object-cover" loading="eager" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-950/70 to-transparent" />
          </motion.div>

          <motion.div
            style={{ y: yImg2 }}
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 0.55 }}
            className="absolute -left-10 bottom-24 hidden h-36 w-48 -rotate-2 overflow-hidden rounded-2xl border border-white/10 shadow-2xl animate-float xl:block"
          >
            <img src={IMG2} alt="Rooftop solar panels" className="h-full w-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-950/70 to-transparent" />
          </motion.div>

          <motion.div
            id="estimator"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.5 }}
            className="glass-card relative z-10 mt-16 rounded-3xl p-7 shadow-2xl lg:mt-24"
            data-testid="hero-estimator"
          >
            <div className="mb-5 flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-solar/15">
                <Sun size={18} className="text-solar-yellow" />
              </div>
              <h3 className="font-display text-lg font-bold text-white">{t.hero.estTitle}</h3>
            </div>

            <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">{t.hero.estBill}</label>
            <div className="mt-1 font-mono text-3xl font-bold text-solar-highlight">₹{bill.toLocaleString("en-IN")}</div>
            <input
              type="range"
              min="1000"
              max="20000"
              step="500"
              value={bill}
              onChange={(e) => setBill(Number(e.target.value))}
              className="mt-4 w-full accent-amber-500"
              data-testid="estimator-bill-slider"
            />

            <div className="mt-6 space-y-3 rounded-2xl border border-white/10 bg-navy-950/60 p-5">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-400">{t.hero.estSize}</span>
                <span className="font-mono font-bold text-white">{kw} kW</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-400">{t.hero.estSubsidy}</span>
                <span className="font-mono font-bold text-eco-bright">₹{subsidy.toLocaleString("en-IN")}</span>
              </div>
              <div className="flex items-center justify-between border-t border-white/10 pt-3 text-sm">
                <span className="text-slate-400">{t.hero.estSave}</span>
                <span className="font-mono font-bold text-solar-highlight">₹{savings.toLocaleString("en-IN")}</span>
              </div>
            </div>

            <a
              href={`https://wa.me/918922004004?text=${encodeURIComponent(`Hello Uma Enterprises! My monthly bill is ₹${bill}. Estimator suggests ~${kw} kW system with ₹${subsidy} subsidy. Please send an exact quote.`)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="estimator-quote-btn"
              className="mt-5 block rounded-full bg-eco px-6 py-3 text-center text-sm font-bold text-amber-950 transition-colors duration-300 hover:bg-eco-bright"
            >
              {t.hero.estCta}
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
